// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    description = "description",
    placeholder = "placeholder",
    disclaimer = "disclaimer",
    alignment = "alignment",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.disclaimer]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: GridItemProps;
    disclaimerText?: TypographyPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        gap: 0,
        css: css`
            width: 100%;
            max-width: 550px !important;
            display: flex;

            @media (max-width: 768px) {
                max-width: 100% !important;
                margin-bottom: 20px;
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    titleLabel: {
        variant: "h5",
        color: "common.white",
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    descriptionText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: ${fontConfig.spacing.small};
            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            background: transparent;
            border-radius: 0;
            margin-bottom: 30px;
            min-width: 275px;
            button {
                span {
                    color: ${getColor("text.main")} !important;
                }
            }
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                width: calc(100% - 114px);
            `,
            inputSelect: css`
                height: 32px;
                border: 0;
                &:not[aria-invalid="true"] {
                    border: 0;
                }

                &:focus {
                    border: 0;
                }
            `,
        },
    },
    emailButton: {
        css: css`
            margin: 0;
            min-width: 114px;
            padding-left: 15px;
            padding-right: 15px;
            border: 0 !important;
            box-shadow: none !important;
            outline: 0 !important;
        `,
    },
    disclaimerGridItem: {
        width: 12,
    },
    disclaimerText: {
        size:12,
        lineHeight: fontConfig.lineHeight.singleLinexSmall,
        color: "common.white",
        css: css`
            padding: 15px;
        `,
    },
};

const styles = subscribeStyles;

const CmsSubscribe = ({ fields, subscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        subscribe({
            email,
            isSubscribedToHubSpot: true,
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    emailSubscriptionNotificationHandler(siteMessage("Email_Subscribe_Success"), "success");
                } else {
                    emailSubscriptionNotificationHandler(siteMessage("Custom_EmailSubscribeError"), "danger");
                }
            },
        } as any);
    };

    const emailSubscriptionNotificationHandler = (message: any, messageType: any) => {
        toasterContext.addToast({ body: message, messageType });
    };

    const emailChangeHandler = (email: string) => {
        checkEmail(email);
        setEmail(email);
    };

    const checkEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <GridContainer className="main-grid-container" {...styles.mainGridContainer}>
                <GridItem className="title-grid-item" {...styles.titleGridItem} css={gridItemCss(styles.titleGridItem)}>
                    <Typography className="title" {...styles.titleLabel}>
                        {fields.title}
                    </Typography>
                </GridItem>
                <GridItem
                    className="description-grid-item"
                    {...styles.descriptionGridItem}
                    css={gridItemCss(styles.descriptionGridItem)}
                >
                    <Typography className="description-text" {...styles.descriptionText}>
                        {parse(fields.description, parserOptions)}
                    </Typography>
                </GridItem>
                <GridItem className="email-grid-item" {...styles.emailGridItem} css={gridItemCss(styles.emailGridItem)}>
                    <TextField
                        className="email-text-field"
                        {...styles.emailTextField}
                        id={id}
                        placeholder={fields.placeholder}
                        value={email}
                        maxLength={150}
                        onChange={e => emailChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && emailError}
                    />
                    <Button
                        className="email-button"
                        sizeVariant="small"
                        {...styles.emailButton}
                        onClick={onSubscribeClick}
                        disabled={isSubmitted && !!emailError}
                    >
                        {fields.label}
                    </Button>
                </GridItem>
                {fields.disclaimer && (
                    <GridItem
                        className="disclaimer-grid-item"
                        {...styles.disclaimerGridItem}
                        css={gridItemCss(styles.disclaimerGridItem)}
                    >
                        <Typography className="disclaimer-text" {...styles.disclaimerText}>
                            {parse(fields.disclaimer, parserOptions)}
                        </Typography>
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.disclaimer,
                displayName: "Disclaimer",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
